import React from 'react'

import {
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Button,
  IconButton,
  Typography,
  Divider,
  Box,
  useTheme,
  alpha,
} from '@mui/material'
import { Close as CloseIcon, ErrorOutline as ErrorOutlineIcon, BoltOutlined, TimerOutlined } from '@mui/icons-material'

import { useDisclosure } from '@/hooks/useDisclosure'
import { ChargingSessionInAppNotificationPayload } from '@/lib/in-app-notifications/categories/charging_session'
import { numericUtils } from '@/utils/numeric'
import { chargingUtils } from '@/utils/charging'

import { getElapsedTime } from '../utils/getElapsedTime'

export interface ChargingSessionFailedModalProps {
  modalMethods: ReturnType<typeof useDisclosure>
  payload: ChargingSessionInAppNotificationPayload | null | undefined
  onClose?: () => Promise<void>
}

export const ChargingSessionFailedModal: React.FC<ChargingSessionFailedModalProps> = ({
  modalMethods,
  payload,
  onClose,
}) => {
  const handleClose = async () => {
    modalMethods?.close()
    await onClose?.()
  }

  const theme = useTheme()

  const chargeStartTime = chargingUtils.getChargeStartTime(payload)
  const chargeEndTime = chargingUtils.getChargeEndTime(payload)

  const elapsedTime = getElapsedTime(
    chargeStartTime ? new Date(chargeStartTime) : null,
    chargeEndTime ? new Date(chargeEndTime) : new Date()
  )

  return (
    <Dialog
      open={modalMethods.isOpen}
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 3, padding: 1, minWidth: 320 },
        },
      }}
      onClose={handleClose}
    >
      <IconButton
        aria-label="fechar"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 4,
          top: 4,
        }}
      >
        <CloseIcon color="disabled" sx={{ fontSize: '16px' }} />
      </IconButton>

      <DialogContent>
        <Stack spacing={3} alignItems="center">
          <Stack alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.error.main, 0.1),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 40, color: 'error.main' }} />
            </Box>
            <Typography variant="h5" fontWeight="800" textAlign="center" color="text.primary">
              Falha no carregamento
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Houve um problema e a sessão foi interrompida.
            </Typography>
          </Stack>

          <Divider flexItem />

          <Stack direction="row" spacing={4} justifyContent="center">
            <Stack alignItems="center" spacing={0.5}>
              <TimerOutlined fontSize="large" color="action" />
              <Typography variant="subtitle2" fontWeight="bold">
                {elapsedTime}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" textAlign="center">
                Tempo decorrido
              </Typography>
            </Stack>

            <Stack alignItems="center" spacing={0.5}>
              <BoltOutlined fontSize="large" color="action" />
              <Typography variant="subtitle2" fontWeight="bold">
                {`${numericUtils.formatKwh(payload?.kwh_delivered)} kWh`}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" textAlign="center">
                Energia carregada
              </Typography>
            </Stack>
          </Stack>

          <Divider flexItem />

          <Box
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.08),
              border: '1px dashed',
              borderColor: 'success.main',
              borderRadius: 2,
              p: 2,
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              <Box component="span" color="success.dark" fontWeight="bold">
                Não se preocupe!
              </Box>{' '}
              O valor não utilizado será estornado automaticamente.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleClose}
          sx={{ width: '100%', borderRadius: 4 }}
          disableElevation
        >
          Voltar
        </Button>
      </DialogActions>
    </Dialog>
  )
}
